/**
 * PermissionsContext
 * Sistema de Control de Acceso Basado en Roles (RBAC) para cumplimiento HIPAA.
 *
 * HIPAA Requirement: 45 CFR 164.308(a)(4) - Information Access Management
 *
 * ============================================================================
 * DOCUMENTACIÓN PARA REFACTORIZACIÓN CON BACKEND
 * ============================================================================
 *
 * ESTADO ACTUAL (Frontend Only):
 * - Los roles y permisos están definidos en este archivo
 * - El rol del usuario viene de user.rol del login
 * - Si no hay rol definido, se asigna 'guest' con permisos mínimos
 *
 * CAMBIOS REQUERIDOS EN BACKEND:
 * 1. Agregar campo 'role' al JWT con el rol del usuario
 * 2. Crear endpoint GET /api/permissions para obtener permisos del usuario
 * 3. Crear endpoint GET /api/roles para obtener lista de roles disponibles
 * 4. Implementar validación de permisos en cada endpoint de API
 *
 * REFACTORIZACIÓN FUTURA:
 * 1. Obtener permisos del backend en lugar de definirlos aquí
 * 2. Sincronizar permisos con el backend en cada request
 * 3. Implementar cache de permisos con invalidación
 * 4. Agregar permisos granulares por paciente/location
 *
 * ============================================================================
 */

import React, { createContext, useContext, useMemo, useCallback } from "react";
import { AuthContext } from "../AuthContext/AuthContext";

export const PermissionsContext = createContext();

/**
 * Definición de Roles del Sistema
 * TODO: Mover a backend y obtener via API
 */
export const ROLES = {
  ADMIN: "admin",
  PCP: "pcp",
  CARE_MANAGEMENT: "care_management",
  CEO: "ceo",
  GUEST: "guest",
};

/**
 * Mapeo de roles del backend a roles del sistema
 * Ajustar según los valores que envíe el backend
 */
const BACKEND_ROLE_MAP = {
  "1": ROLES.ADMIN,
  "2": ROLES.PCP,
  "3": ROLES.CARE_MANAGEMENT,
  "4": ROLES.CEO,
  // Mapeos por key (el backend puede enviar roleKey como string)
  admin: ROLES.ADMIN,
  pcp: ROLES.PCP,
  care_management: ROLES.CARE_MANAGEMENT,
  ceo: ROLES.CEO,
};

/**
 * Definición de Permisos por Módulo
 * Formato: "modulo:accion"
 * Acciones: read, write, delete, export, admin
 */
export const PERMISSIONS = {
  // Pacientes
  PATIENTS_READ: "patients:read",
  PATIENTS_WRITE: "patients:write",
  PATIENTS_DELETE: "patients:delete",
  PATIENTS_EXPORT: "patients:export",

  // Care Gaps
  CAREGAPS_READ: "caregaps:read",
  CAREGAPS_WRITE: "caregaps:write",
  CAREGAPS_CLOSE: "caregaps:close",

  // Coding Opportunities
  CODING_READ: "coding:read",
  CODING_WRITE: "coding:write",
  CODING_APPROVE: "coding:approve",

  // Care Plans
  CAREPLANS_READ: "careplans:read",
  CAREPLANS_WRITE: "careplans:write",
  CAREPLANS_APPROVE: "careplans:approve",

  // Clinical Notes
  NOTES_READ: "notes:read",
  NOTES_WRITE: "notes:write",
  NOTES_DELETE: "notes:delete",

  // Specialist Referrals
  REFERRALS_READ: "referrals:read",
  REFERRALS_WRITE: "referrals:write",
  REFERRALS_APPROVE: "referrals:approve",

  // Lab/Phlebotomy
  LAB_READ: "lab:read",
  LAB_WRITE: "lab:write",

  // Reports
  REPORTS_READ: "reports:read",
  REPORTS_EXPORT: "reports:export",

  // AI Features
  AI_USE: "ai:use",
  AI_ADMIN: "ai:admin",

  // Administration
  ADMIN_USERS: "admin:users",
  ADMIN_SETTINGS: "admin:settings",
  ADMIN_AUDIT: "admin:audit",

  // Assignments
  ASSIGNMENTS_READ: "assignments:read",
  ASSIGNMENTS_WRITE: "assignments:write",

  // Documents
  DOCUMENTS_READ: "documents:read",
  DOCUMENTS_WRITE: "documents:write",

  // Support/Debug
  SUPPORT_ACCESS: "support:access",
  SUPPORT_DEBUG: "support:debug",
};

/**
 * Permisos asignados a cada rol
 * TODO: Mover a backend
 */
export const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: ["*"],

  [ROLES.CEO]: ["*"],

  [ROLES.PCP]: [
    PERMISSIONS.PATIENTS_READ,
    PERMISSIONS.PATIENTS_WRITE,
    PERMISSIONS.PATIENTS_EXPORT,
    PERMISSIONS.CAREGAPS_READ,
    PERMISSIONS.CAREGAPS_WRITE,
    PERMISSIONS.CAREGAPS_CLOSE,
    PERMISSIONS.CODING_READ,
    PERMISSIONS.CODING_WRITE,
    PERMISSIONS.NOTES_READ,
    PERMISSIONS.NOTES_WRITE,
    PERMISSIONS.REFERRALS_READ,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.REFERRALS_APPROVE,
    PERMISSIONS.LAB_READ,
    PERMISSIONS.LAB_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.AI_USE,
    PERMISSIONS.ASSIGNMENTS_READ,
    PERMISSIONS.ASSIGNMENTS_WRITE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_WRITE,
    PERMISSIONS.SUPPORT_ACCESS,
  ],

  [ROLES.CARE_MANAGEMENT]: [
    PERMISSIONS.PATIENTS_READ,
    PERMISSIONS.PATIENTS_WRITE,
    PERMISSIONS.CAREGAPS_READ,
    PERMISSIONS.CAREGAPS_WRITE,
    PERMISSIONS.CAREGAPS_CLOSE,
    PERMISSIONS.CAREPLANS_READ,
    PERMISSIONS.CAREPLANS_WRITE,
    PERMISSIONS.NOTES_READ,
    PERMISSIONS.NOTES_WRITE,
    PERMISSIONS.REFERRALS_READ,
    PERMISSIONS.REFERRALS_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.AI_USE,
    PERMISSIONS.DOCUMENTS_READ,
    PERMISSIONS.DOCUMENTS_WRITE,
  ],

  [ROLES.GUEST]: [],
};

/**
 * Permisos requeridos por ruta
 * Usado por ProtectedRoute para validar acceso
 */
export const ROUTE_PERMISSIONS = {
  "/Main": null, // Accesible para todos los autenticados
  "/PatientMap": PERMISSIONS.PATIENTS_READ,
  "/ClinicalInfo": PERMISSIONS.PATIENTS_READ,
  "/care-gaps": PERMISSIONS.CAREGAPS_READ,
  "/coding-opportunities": PERMISSIONS.CODING_READ,
  "/Careplan": PERMISSIONS.CAREPLANS_READ,
  "/specialist-referrals": PERMISSIONS.REFERRALS_READ,
  "/LabPhlebotomy": PERMISSIONS.LAB_READ,
  "/ClinicalNoteAi": PERMISSIONS.AI_USE,
  "/support": PERMISSIONS.SUPPORT_ACCESS,
  "/assignments": PERMISSIONS.ASSIGNMENTS_READ,
  "/profile": null, // Todos pueden ver su perfil
  "/library": null, // Accesible para todos los autenticados
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);

  /**
   * Obtener rol normalizado del usuario
   */
  const userRole = useMemo(() => {
    if (!user?.rol) return ROLES.GUEST;

    const backendRole = String(user.rol).toLowerCase();
    return BACKEND_ROLE_MAP[backendRole] || BACKEND_ROLE_MAP[user.rol] || ROLES.GUEST;
  }, [user?.rol]);

  /**
   * Obtener permisos del usuario según su rol
   */
  const userPermissions = useMemo(() => {
    return ROLE_PERMISSIONS[userRole] || [];
  }, [userRole]);

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!permission) return true; // Sin permiso requerido = acceso libre
      if (userPermissions.includes("*")) return true; // Admin tiene todo
      return userPermissions.includes(permission);
    },
    [userPermissions]
  );

  /**
   * Verificar si el usuario tiene ALGUNO de los permisos (OR)
   */
  const hasAnyPermission = useCallback(
    (permissions) => {
      if (!permissions || permissions.length === 0) return true;
      if (userPermissions.includes("*")) return true;
      return permissions.some((p) => userPermissions.includes(p));
    },
    [userPermissions]
  );

  /**
   * Verificar si el usuario tiene TODOS los permisos (AND)
   */
  const hasAllPermissions = useCallback(
    (permissions) => {
      if (!permissions || permissions.length === 0) return true;
      if (userPermissions.includes("*")) return true;
      return permissions.every((p) => userPermissions.includes(p));
    },
    [userPermissions]
  );

  /**
   * Verificar si el usuario puede acceder a una ruta
   */
  const canAccessRoute = useCallback(
    (path) => {
      const requiredPermission = ROUTE_PERMISSIONS[path];
      return hasPermission(requiredPermission);
    },
    [hasPermission]
  );

  /**
   * Verificar si es admin
   */
  const isAdmin = useMemo(() => {
    return userRole === ROLES.ADMIN || userRole === ROLES.CEO || userPermissions.includes("*");
  }, [userRole, userPermissions]);

  const value = useMemo(
    () => ({
      // Estado
      userRole,
      userPermissions,
      isAdmin,
      // Funciones de verificación
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
      // Constantes exportadas
      ROLES,
      PERMISSIONS,
      ROUTE_PERMISSIONS,
    }),
    [
      userRole,
      userPermissions,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook para usar el contexto de permisos
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions debe usarse dentro de PermissionsProvider");
  }
  return context;
};

/**
 * HOC para proteger componentes por permiso
 */
export const withPermission = (WrappedComponent, requiredPermission) => {
  return function PermissionWrapper(props) {
    const { hasPermission } = usePermissions();

    if (!hasPermission(requiredPermission)) {
      return null; // O un componente de "Sin acceso"
    }

    return <WrappedComponent {...props} />;
  };
};

export default PermissionsContext;
